import ReactApexChart from "react-apexcharts";
import { formatMoney, formatNumber } from "./customerHelpers";

/**
 * Gráficos do painel da carteira.
 *
 * A paleta abaixo foi validada (banda de luminosidade, piso de croma,
 * separação sob daltonismo e contraste) contra o fundo branco dos cards.
 * Não troque um hex isolado sem revalidar o conjunto.
 */

// Categórica — identidade de séries. Ordem fixa, nunca ciclada.
export const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a"];

// Fora da paleta categórica de 3 cores, para o plano de topo ler como topo.
export const PLAN_DIAMOND_COLOR = "#7c4dcc";

// Ordinal de um hue só — magnitude ordenada (urgente → distante).
export const ORDINAL_BLUE = [
  "#0d366b",
  "#184f95",
  "#2a78d6",
  "#5598e7",
  "#86b6ef",
];

const INK_MUTED = "#898781";
const GRIDLINE = "#e1e0d9";
const SURFACE = "#ffffff";

const baseChart = {
  fontFamily: "inherit",
  toolbar: { show: false },
  zoom: { enabled: false },
  animations: { easing: "easeout", speed: 400 },
  parentHeightOffset: 0,
};

const axisLabelStyle = {
  colors: INK_MUTED,
  fontSize: "11px",
  fontWeight: 500,
};

const ChartCard = ({ title, hint, legend, children, footer }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      </div>
      {legend ? (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          {legend.map((item) => (
            <span key={item.label} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
    {children}
    {footer}
  </section>
);

/** Tendência ao longo do tempo, duas séries distintas → linhas. */
export const UsageTrendChart = ({ series }) => {
  const categories = series.map((item) => item.label);

  const options = {
    chart: { ...baseChart, type: "line" },
    colors: [SERIES_COLORS[0], SERIES_COLORS[1]],
    stroke: { width: 2, curve: "smooth" },
    markers: { size: 4, strokeWidth: 2, strokeColors: SURFACE, hover: { size: 6 } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: GRIDLINE,
      strokeDashArray: 4,
      padding: { left: 8, right: 8, top: 0 },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories,
      labels: { style: axisLabelStyle },
      axisBorder: { color: "#c3c2b7" },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        style: axisLabelStyle,
        formatter: (value) => formatNumber(Math.round(value)),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (value) => formatNumber(value) },
    },
  };

  return (
    <ChartCard
      title="Uso da carteira"
      hint="Agendamentos e comandas lançados nos últimos 6 meses"
      legend={[
        { label: "Agendamentos", color: SERIES_COLORS[0] },
        { label: "Comandas", color: SERIES_COLORS[1] },
      ]}
    >
      <ReactApexChart
        type="line"
        height={230}
        options={options}
        series={[
          { name: "Agendamentos", data: series.map((item) => item.appointments) },
          { name: "Comandas", data: series.map((item) => item.commands) },
        ]}
      />
    </ChartCard>
  );
};

/** Uma série só de magnitude → barras em um hue único. */
export const RevenueChart = ({ series }) => {
  const options = {
    chart: { ...baseChart, type: "bar" },
    colors: [SERIES_COLORS[0]],
    plotOptions: {
      bar: {
        columnWidth: "45%",
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: GRIDLINE,
      strokeDashArray: 4,
      padding: { left: 8, right: 8, top: 0 },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: series.map((item) => item.label),
      labels: { style: axisLabelStyle },
      axisBorder: { color: "#c3c2b7" },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: axisLabelStyle,
        formatter: (value) =>
          value >= 1000 ? `${Math.round(value / 1000)}k` : Math.round(value),
      },
    },
    tooltip: { y: { formatter: (value) => formatMoney(value) } },
  };

  return (
    <ChartCard
      title="Faturamento dos salões"
      hint="Total lançado em comandas, mês a mês"
    >
      <ReactApexChart
        type="bar"
        height={230}
        options={options}
        series={[{ name: "Faturamento", data: series.map((item) => item.revenue) }]}
      />
    </ChartCard>
  );
};

/** Parte-do-todo com poucas categorias → barra empilhada horizontal. */
const StackedShareChart = ({ title, hint, slices, total }) => {
  const options = {
    chart: { ...baseChart, type: "bar", stacked: true, stackType: "100%" },
    colors: slices.map((slice) => slice.color),
    plotOptions: {
      bar: { horizontal: true, barHeight: "58%", borderRadius: 4 },
    },
    // Separador de 2px na cor da superfície entre os segmentos.
    stroke: { width: 2, colors: [SURFACE] },
    dataLabels: {
      enabled: true,
      formatter: (value) => (value >= 8 ? `${Math.round(value)}%` : ""),
      style: { fontSize: "11px", fontWeight: 600, colors: [SURFACE] },
    },
    legend: { show: false },
    grid: { show: false, padding: { left: 0, right: 0, top: -20, bottom: -20 } },
    xaxis: {
      categories: [""],
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { show: false } },
    tooltip: {
      y: {
        formatter: (value, { seriesIndex }) =>
          `${formatNumber(slices[seriesIndex].value)} de ${formatNumber(total)}`,
      },
    },
  };

  return (
    <ChartCard
      title={title}
      hint={hint}
      legend={slices.map((slice) => ({
        label: slice.label,
        color: slice.color,
      }))}
      footer={
        <div className="mt-1 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
          {slices.map((slice) => (
            <div key={slice.label}>
              <p className="truncate text-[11px] text-slate-500">{slice.label}</p>
              <p className="text-lg font-bold leading-tight text-slate-900">
                {formatNumber(slice.value)}
              </p>
            </div>
          ))}
        </div>
      }
    >
      <ReactApexChart
        type="bar"
        height={92}
        options={options}
        series={slices.map((slice) => ({
          name: slice.label,
          data: [slice.value],
        }))}
      />
    </ChartCard>
  );
};

export const PlanMixChart = ({ plans, total }) => (
  <StackedShareChart
    title="Composição por plano"
    hint="Distribuição dos clientes da carteira"
    total={total}
    slices={[
      // O Diamond entra primeiro: a fatia mais valiosa da carteira não pode
      // ficar invisível no gráfico por não estar listada aqui.
      { label: "Diamond", value: plans?.DIAMOND || 0, color: PLAN_DIAMOND_COLOR },
      { label: "Pro", value: plans?.PRO || 0, color: SERIES_COLORS[0] },
      { label: "Essential", value: plans?.ESSENTIAL || 0, color: SERIES_COLORS[1] },
      { label: "Free", value: plans?.FREE || 0, color: SERIES_COLORS[2] },
    ]}
  />
);

export const EngagementChart = ({ health, total, rate }) => (
  <StackedShareChart
    title="Engajamento"
    hint={`${rate ?? 0}% da carteira usou o sistema nos últimos 30 dias`}
    total={total}
    slices={[
      { label: "Usando", value: health?.ACTIVE || 0, color: SERIES_COLORS[2] },
      { label: "Em risco", value: health?.AT_RISK || 0, color: SERIES_COLORS[1] },
      { label: "Nunca usou", value: health?.NEVER_USED || 0, color: SERIES_COLORS[0] },
    ]}
  />
);

/** Magnitude em faixas ordenadas → barras horizontais em rampa de um hue. */
export const RenewalChart = ({ buckets }) => {
  const options = {
    chart: { ...baseChart, type: "bar" },
    colors: ORDINAL_BLUE,
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "58%",
        distributed: true,
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      offsetX: 8,
      formatter: (value) => formatNumber(value),
      style: { fontSize: "11px", fontWeight: 600, colors: ["#52514e"] },
    },
    legend: { show: false },
    grid: {
      borderColor: GRIDLINE,
      strokeDashArray: 4,
      padding: { left: 0, right: 16, top: -8 },
      yaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: buckets.map((bucket) => bucket.label),
      labels: { style: axisLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: axisLabelStyle } },
    tooltip: {
      y: { formatter: (value) => `${formatNumber(value)} cliente(s)` },
    },
  };

  return (
    <ChartCard
      title="Quando vencem"
      hint="Clientes por janela de renovação"
    >
      <ReactApexChart
        type="bar"
        height={230}
        options={options}
        series={[{ name: "Clientes", data: buckets.map((bucket) => bucket.count) }]}
      />
    </ChartCard>
  );
};

/**
 * Evolução de agendamentos desde o cadastro do cliente.
 *
 * Série única: o título já nomeia o dado, então não leva legenda. Com muitos
 * meses os marcadores poluem a linha — a leitura ponto a ponto fica no
 * tooltip com crosshair.
 */
export const LifetimeAppointmentsChart = ({ series }) => {
  if (!series?.length) {
    return (
      <ChartCard title="Evolução de agendamentos">
        <p className="py-8 text-center text-sm text-slate-500">
          Sem histórico registrado.
        </p>
      </ChartCard>
    );
  }

  const total = series.reduce((acc, item) => acc + item.appointments, 0);
  const best = series.reduce(
    (acc, item) => (item.appointments > acc.appointments ? item : acc),
    series[0]
  );
  const average = Math.round(total / series.length);
  const showEveryOtherLabel = series.length > 14;

  const options = {
    chart: { ...baseChart, type: "area" },
    colors: [SERIES_COLORS[0]],
    stroke: { width: 2, curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.28,
        opacityTo: 0.02,
        stops: [0, 100],
      },
    },
    markers: { size: 0, hover: { size: 5 } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: GRIDLINE,
      strokeDashArray: 4,
      padding: { left: 8, right: 8, top: 0 },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: series.map((item) => item.label),
      tickAmount: showEveryOtherLabel ? Math.min(12, series.length) : undefined,
      labels: { style: axisLabelStyle, rotate: 0, hideOverlappingLabels: true },
      axisBorder: { color: "#c3c2b7" },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      crosshairs: { show: true, stroke: { color: GRIDLINE, width: 1 } },
    },
    yaxis: {
      labels: {
        style: axisLabelStyle,
        formatter: (value) => formatNumber(Math.round(value)),
      },
    },
    tooltip: {
      x: { formatter: (_, { dataPointIndex }) => series[dataPointIndex]?.label },
      y: { formatter: (value) => `${formatNumber(value)} agendamentos` },
    },
  };

  return (
    <ChartCard
      title="Evolução de agendamentos"
      hint={`Todos os meses desde o cadastro · ${series[0].label} até ${
        series[series.length - 1].label
      }`}
      footer={
        <div className="mt-1 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
          <div>
            <p className="text-[11px] text-slate-500">Total no período</p>
            <p className="text-lg font-bold leading-tight text-slate-900">
              {formatNumber(total)}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500">Média por mês</p>
            <p className="text-lg font-bold leading-tight text-slate-900">
              {formatNumber(average)}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500">Melhor mês</p>
            <p className="text-lg font-bold leading-tight text-slate-900">
              {formatNumber(best.appointments)}{" "}
              <span className="text-xs font-normal text-slate-500">
                ({best.label})
              </span>
            </p>
          </div>
        </div>
      }
    >
      <ReactApexChart
        type="area"
        height={240}
        options={options}
        series={[{ name: "Agendamentos", data: series.map((item) => item.appointments) }]}
      />
    </ChartCard>
  );
};
